import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DeliveryType } from '@payer-admin-ui/api/src/invoices/models/DeliveryType';
import {
  FetchingStateWithDoNotRepeat,
  InvoiceAggregationNumberAndDeliveryTypeObject,
  InvoiceAggregationNumberObject,
} from '@payer-admin-ui/components-v2/src/types/state';
import i18n from 'i18next';
import { fetchInvoiceAggregationsPage } from '../../views/Pages/invoiceAggregations/invoiceAggregationsGlobalState';
import { MerchantAdminDispatch, MerchantAdminState } from '../../redux/store';
import { invoiceAggregationAPI } from '../../redux/invoiceAggregationAPI';
import { showSnackbar } from '../../redux/snackbar';

export const sendCopy = createAsyncThunk<void, InvoiceAggregationNumberAndDeliveryTypeObject>(
  'invoiceAggregations/sendCopy',
  async ({ invoiceAggregationNumber }) => {
    await invoiceAggregationAPI.actions.sendCopy({ invoiceAggregationNumber });
  },
);

export const sendReminder = createAsyncThunk<
  void,
  InvoiceAggregationNumberAndDeliveryTypeObject,
  { dispatch: MerchantAdminDispatch; state: MerchantAdminState }
>('invoiceAggregations/sendReminder', async ({ invoiceAggregationNumber, deliveryType }, { dispatch, getState }) => {
  await invoiceAggregationAPI.actions.sendReminder({ invoiceAggregationNumber, deliveryType });
  dispatch(fetchInvoiceAggregationsPage());
});

export const cancelReminders = createAsyncThunk<
  void,
  InvoiceAggregationNumberObject,
  { dispatch: MerchantAdminDispatch; state: MerchantAdminState }
>('invoiceAggregations/cancelReminders', async ({ invoiceAggregationNumber }, { dispatch, getState }) => {
  await invoiceAggregationAPI.actions.cancelReminders({ invoiceAggregationNumber });
  dispatch(fetchInvoiceAggregationsPage());
});

export const markStatementAsManuallyPaid = createAsyncThunk<
  void,
  { invoiceAggregationNumber: number; bookingDate: string; externalReference?: string },
  { dispatch: MerchantAdminDispatch; state: MerchantAdminState }
>(
  'invoiceAggregations/markAsManuallyPaid',
  async ({ invoiceAggregationNumber, externalReference, bookingDate }, { dispatch }) => {
    await invoiceAggregationAPI.actions.markAsManuallyPaid({
      invoiceAggregationNumber,
      externalReference,
      bookingDate,
    });
    dispatch(showSnackbar(i18n.t('invoiceAggregations.actions.markAsManuallyPaid.success'), { severity: 'success' }));
    dispatch(fetchInvoiceAggregationsPage());
  },
);

export type InvoiceAggregationsActionsState = {
  sendCopyState: {
    [invoiceAggregationNumber in string]?: { [deliveryType in DeliveryType]?: FetchingStateWithDoNotRepeat };
  };
  sendReminderState: { [invoiceAggregationNumber in string]?: FetchingStateWithDoNotRepeat };
  cancelRemindersState: { [invoiceAggregationNumber in string]?: FetchingStateWithDoNotRepeat };
  markAsManuallyPaidState: { [invoiceAggregationNumber in string]?: FetchingStateWithDoNotRepeat };
  actionError: any;
};

const invoiceActionsSlice = createSlice({
  name: 'invoiceAggregationActions',
  initialState: {
    sendCopyState: {},
    sendReminderState: {},
    cancelRemindersState: {},
    markAsManuallyPaidState: {},
    actionError: null,
  } as InvoiceAggregationsActionsState,
  reducers: {
    resetError: state => {
      state.actionError = null;
    },
    resetSendCopySuccessState: (
      state,
      {
        payload: { invoiceAggregationNumber, deliveryType },
      }: PayloadAction<{ invoiceAggregationNumber: number; deliveryType: DeliveryType }>,
    ) => {
      delete state.sendCopyState[invoiceAggregationNumber]?.[deliveryType]?.success;
    },
    resetSendReminderSuccessState: (
      state,
      { payload: { invoiceAggregationNumber } }: PayloadAction<{ invoiceAggregationNumber: number }>,
    ) => {
      delete state.sendReminderState[invoiceAggregationNumber]?.success;
    },
    resetCancelRemindersSuccessState: (
      state,
      { payload: { invoiceAggregationNumber } }: PayloadAction<{ invoiceAggregationNumber: number }>,
    ) => {
      delete state.cancelRemindersState[invoiceAggregationNumber]?.success;
      delete state.cancelRemindersState[invoiceAggregationNumber]?.doNotRepeat;
    },
  },
  extraReducers: builder => {
    builder.addCase(sendCopy.pending, (state, { meta: { arg } }) => {
      state.sendCopyState[arg.invoiceAggregationNumber] = {
        ...state.sendCopyState[arg.invoiceAggregationNumber],
        [arg.deliveryType]: { isFetching: true, success: undefined },
      };
    });
    builder.addCase(sendCopy.fulfilled, (state, { meta: { arg } }) => {
      state.sendCopyState[arg.invoiceAggregationNumber] = {
        ...state.sendCopyState[arg.invoiceAggregationNumber],
        [arg.deliveryType]: { isFetching: false, success: true },
      };
    });
    builder.addCase(sendCopy.rejected, (state, { meta: { arg }, error }) => {
      state.sendCopyState[arg.invoiceAggregationNumber] = {
        ...state.sendCopyState[arg.invoiceAggregationNumber],
        [arg.deliveryType]: { success: undefined, isFetching: false },
      };
      state.actionError = error;
    });

    builder.addCase(sendReminder.pending, (state, { meta: { arg } }) => {
      state.sendReminderState[arg.invoiceAggregationNumber] = { isFetching: true, success: undefined };
    });
    builder.addCase(sendReminder.fulfilled, (state, { meta: { arg } }) => {
      state.sendReminderState[arg.invoiceAggregationNumber] = { isFetching: false, success: true };
    });
    builder.addCase(sendReminder.rejected, (state, { meta: { arg }, error }) => {
      state.sendReminderState[arg.invoiceAggregationNumber] = { isFetching: false, success: undefined };
      state.actionError = error;
    });

    builder.addCase(cancelReminders.pending, (state, { meta: { arg } }) => {
      state.cancelRemindersState[arg.invoiceAggregationNumber] = { isFetching: true, success: undefined };
    });
    builder.addCase(cancelReminders.fulfilled, (state, { meta: { arg } }) => {
      state.cancelRemindersState[arg.invoiceAggregationNumber] = { isFetching: false, success: true };
    });
    builder.addCase(cancelReminders.rejected, (state, { meta: { arg }, error }) => {
      state.cancelRemindersState[arg.invoiceAggregationNumber] = { isFetching: false, success: undefined };
      state.actionError = error;
    });
    builder.addCase(markStatementAsManuallyPaid.pending, (state, { meta: { arg } }) => {
      state.markAsManuallyPaidState[arg.invoiceAggregationNumber] = { isFetching: true, success: undefined };
    });
    builder.addCase(markStatementAsManuallyPaid.fulfilled, (state, { meta: { arg } }) => {
      state.markAsManuallyPaidState[arg.invoiceAggregationNumber] = { isFetching: false, success: true };
    });
    builder.addCase(markStatementAsManuallyPaid.rejected, (state, { meta: { arg }, error }) => {
      state.markAsManuallyPaidState[arg.invoiceAggregationNumber] = { isFetching: false, success: undefined };
      state.actionError = error;
    });
  },
});

export const {
  actions: { resetError, resetSendCopySuccessState, resetSendReminderSuccessState, resetCancelRemindersSuccessState },
  reducer: invoiceAggregationActionsReducer,
} = invoiceActionsSlice;
